import createClient from 'openapi-fetch';

import type { components, paths } from './schema';
import { readSse } from './sse';

// `fetch` is looked up per call (not captured at load) and the base URL is absolute, so the
// same client works in the browser and under test.
export const api = createClient<paths>({ baseUrl: globalThis.location?.origin ?? '/', fetch: (req) => globalThis.fetch(req) });

export type StartInfo = components['schemas']['StartInfo'];
export type Organisation = components['schemas']['Organisation'];
export type SessionView = components['schemas']['SessionView'];
export type Notice = components['schemas']['Notice'];
export type TrustPage = components['schemas']['TrustPage'];
export type Step = NonNullable<SessionView['step']>;
/** A measure as the participant sees it (ADR 0002). */
export type Measure = Step['questions'][number];

export type Action =
  | { type: 'open' }
  | { type: 'message'; text: string }
  | { type: 'answer'; measureId: string; value: number | string | string[] | null }
  | { type: 'confirm'; phase: string }
  | { type: 'correct'; phase: string; text: string }
  | { type: 'skip' };

export type StreamEvent =
  | { type: 'text'; delta: string }
  | { type: 'widget' }
  /** `decision: 'checkpoint'`: a summary to confirm is being written next. */
  | { type: 'turn_end'; decision?: string }
  | { type: 'checkpoint' }
  | { type: 'completed' };

/** An API error code (`busy`, `closed`, …) the UI can translate. */
export class ApiError extends Error {
  constructor(readonly code: string) {
    super(code);
  }
}

/** Runs a streaming action; calls `onEvent` per event and resolves when the stream ends. */
export async function act(action: Action, onEvent: (e: StreamEvent) => void): Promise<void> {
  const res = await fetch('/api/p/sessions/current/actions', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(action),
  });
  if (!res.ok || !res.body) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(body.error ?? 'generic');
  }
  for await (const e of readSse(res.body)) {
    if (e.event === 'error') throw new ApiError((JSON.parse(e.data) as { error: string }).error);
    if (e.event === 'done') return;
    onEvent(JSON.parse(e.data) as StreamEvent);
  }
}
